import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useCouponsForMails,
  useAllCouponsForMails,
  useSendMails,
  useSendDebtMails,
} from '../../../hooks/useSubscribers';
import { usePlans } from '../../../hooks/usePlans';
import { useGroups } from '../../../hooks/useGroups';
import { ITEMS_PER_PAGE } from '../constants/couponsMailsConstants';
import { buildDebtEmailsPayload } from '../utils/couponsMailsUtils';

const getCurrentMonth = () => (new Date().getMonth() + 1).toString();
const getCurrentQuarter = () => Math.ceil((new Date().getMonth() + 1) / 3).toString();

export const useCouponsMailsTable = () => {
  const { data: plans = [] } = usePlans();
  const { data: groups = [] } = useGroups();

  const [page, setPage] = useState(1);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [bulkConfirmationDialogOpen, setBulkConfirmationDialogOpen] = useState(false);
  const [emailResultsDialogOpen, setEmailResultsDialogOpen] = useState(false);
  const [selectedEmailData, setSelectedEmailData] = useState({});
  const [emailResults, setEmailResults] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [includeAllSubscribers, setIncludeAllSubscribers] = useState(false);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  // Estados separados para los filtros del formulario y la búsqueda
  const [filter, setFilter] = useState({
    timePeriod: 'month',
    quarter: '',
    month: getCurrentMonth(),
    year: new Date().getFullYear().toString(),
    plan: '',
    group: '',
    order: '',
    notCancelled: true,
    mailCouponEnabled: true,
    cuotaPura: '',
  });

  const [appliedFilters, setAppliedFilters] = useState({});

  // Actualizar filtros cuando plans esté disponible
  useEffect(() => {
    if (plans.length > 0 && !filter.plan) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        plan: plans[0].uuid,
      }));
    }
  }, [plans, filter.plan]);

  // Query unificada para obtener datos según los filtros aplicados
  const { data: couponsResponse, isLoading: isCouponsLoading } = useCouponsForMails(
    appliedFilters,
    { enabled: searchTriggered && Object.keys(appliedFilters).length > 0 }
  );

  const allCouponsFilters = useMemo(() => {
    const { page: _page, per_page: _perPage, cuotaPura: _cuotaPura, ...rest } = appliedFilters;
    return rest;
  }, [appliedFilters]);

  const {
    data: allCouponsResponse,
    isLoading: isAllCouponsLoading,
    isFetching: isAllCouponsFetching,
  } = useAllCouponsForMails(allCouponsFilters, {
    enabled:
      includeAllSubscribers &&
      searchTriggered &&
      Object.keys(allCouponsFilters).length > 0,
  });

  const sendMailsMutation = useSendMails();
  const sendDebtMailsMutation = useSendDebtMails();

  // Datos procesados
  const data = couponsResponse?.subscribers || [];
  const totalPages = couponsResponse?.pages || 0;
  const totalCount = couponsResponse?.total || 0;
  const allCouponsData = allCouponsResponse?.subscribers || [];
  const allCouponsTotal = allCouponsResponse?.total ?? totalCount;
  const bulkItems = includeAllSubscribers ? allCouponsData : selectedItems;
  const bulkTotalCount = includeAllSubscribers ? allCouponsTotal : selectedItems.length;
  const isAllSubscribersLoading =
    includeAllSubscribers && (isAllCouponsLoading || isAllCouponsFetching);
  const isSendingBulk = sendMailsMutation.isPending || sendDebtMailsMutation.isPending;
  const canBulkSend =
    includeAllSubscribers
      ? !isAllSubscribersLoading && allCouponsData.length > 0
      : selectedItems.length > 0;

  const isLoading = isCouponsLoading || isSendingBulk;

  const buildApiFilters = useCallback(
    (sourceFilter, targetPage = 1, targetRowsPerPage = rowsPerPage) => {
      const apiFilter = { ...sourceFilter, page: targetPage, per_page: targetRowsPerPage };

      if (Object.prototype.hasOwnProperty.call(apiFilter, 'mailCouponEnabled')) {
        apiFilter.mail_coupon_enabled = apiFilter.mailCouponEnabled;
      }

      if (apiFilter.order) {
        apiFilter.contract_number = apiFilter.order;
        delete apiFilter.order;
      }

      Object.keys(apiFilter).forEach((key) => {
        if (apiFilter[key] === '' || apiFilter[key] === null || apiFilter[key] === undefined) {
          delete apiFilter[key];
        }
      });

      if (apiFilter.group === 'todos' || apiFilter.group === 'all') {
        delete apiFilter.group;
      }

      delete apiFilter.cuotaPura;

      return apiFilter;
    },
    [rowsPerPage]
  );

  const handleFilter = useCallback((event) => {
    const { name, value, checked, type } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;

    let newFilter = {
      ...filter,
      [name]: nextValue,
    };

    // Resetear filtros relacionados con período de tiempo
    if (name === 'timePeriod') {
      if (value === 'quarter') {
        newFilter = { ...newFilter, month: '', quarter: getCurrentQuarter() };
      } else if (value === 'month') {
        newFilter = { ...newFilter, quarter: '', month: getCurrentMonth() };
      }
    }

    setFilter(newFilter);

    if (name === 'mailCouponEnabled' && searchTriggered) {
      setAppliedFilters(buildApiFilters(newFilter, 1, rowsPerPage));
      setPage(1);
      setSelectedItems([]);
      setIncludeAllSubscribers(false);
    }
  }, [buildApiFilters, filter, rowsPerPage, searchTriggered]);

  const handleSearch = useCallback(() => {
    // Aplicar los filtros para disparar las queries
    setAppliedFilters(buildApiFilters(filter, 1, rowsPerPage));
    setSearchTriggered(true);
    setPage(1);
    setSelectedItems([]);
    setIncludeAllSubscribers(false);
  }, [buildApiFilters, filter, rowsPerPage]);

  const handlePageChange = useCallback(
    (event, value) => {
      setPage(value);

      // Actualizar los filtros aplicados con la nueva página
      if (Object.keys(appliedFilters).length > 0) {
        setAppliedFilters((prev) => ({
          ...prev,
          page: value,
          per_page: rowsPerPage,
        }));
      }
    },
    [appliedFilters, rowsPerPage]
  );

  const handleRowsPerPageChange = useCallback(
    (event) => {
      const value = parseInt(event.target.value, 10);
      if (!Number.isNaN(value)) {
        setRowsPerPage(value);
        setPage(1);
        if (Object.keys(appliedFilters).length > 0) {
          setAppliedFilters((prev) => ({
            ...prev,
            page: 1,
            per_page: value,
          }));
          setSearchTriggered(true);
        }
      }
    },
    [appliedFilters]
  );

  const handleSendEmail = useCallback((emailData) => {
    setSelectedEmailData(emailData);
    setEmailDialogOpen(true);
  }, []);

  const handleSelectItem = useCallback((item) => {
    setSelectedItems((prevSelectedItems) => {
      const getItemKey = (mailingItem) =>
        `${mailingItem.subscriber_uuid || ''}-${
          mailingItem.contract?.uuid || mailingItem.contract_number || ''
        }`;
      const itemKey = getItemKey(item);
      const isSelected = prevSelectedItems.some(
        (selectedItem) => getItemKey(selectedItem) === itemKey
      );

      if (isSelected) {
        return prevSelectedItems.filter((selectedItem) => getItemKey(selectedItem) !== itemKey);
      } else {
        return [...prevSelectedItems, item];
      }
    });
  }, []);

  const handleIncludeAllSubscribersChange = useCallback((event) => {
    const checked = event.target.checked;
    setIncludeAllSubscribers(checked);
    setSelectedItems([]);
  }, []);

  const handleBulkSend = useCallback(() => {
    if (!canBulkSend) {
      return;
    }

    setBulkConfirmationDialogOpen(true);
  }, [canBulkSend]);

  const handleBulkSendConfirmation = useCallback(
    async (confirmedEmails) => {
      const normalizeDebtResult = (result) => {
        const successfulDetails = Array.isArray(result?.successful_details)
          ? result.successful_details
          : [];
        const failedDetails = Array.isArray(result?.failed_details) ? result.failed_details : [];

        const toEmailEntry = (item) => ({
          email: item.email || item.recipients || 'Sin email',
          order_number: item.contrato_id || item.order_number || 'Sin contrato',
          uuid_for_file: item.uuid_for_file || 'Desconocido',
          error: item.error,
        });

        return {
          message: result?.message || 'Proceso de envío completado.',
          summary: {
            total_emails: result?.total_emails ?? result?.summary?.total_emails ?? 0,
            successful_count:
              result?.successful_emails ??
              result?.summary?.successful_count ??
              successfulDetails.length,
            failed_count:
              result?.failed_emails ?? result?.summary?.failed_count ?? failedDetails.length,
          },
          successful_emails: successfulDetails.map(toEmailEntry),
          failed_emails: failedDetails.map(toEmailEntry),
        };
      };

      const parseCuotaPura = (value) => {
        if (value === undefined || value === null || `${value}`.trim() === '') return null;
        const parsed = Number(`${value}`.replace(',', '.'));
        return Number.isNaN(parsed) ? null : parsed;
      };

      try {
        let result;
        if (!filter.notCancelled) {
          const cuotaPuraValue = parseCuotaPura(filter.cuotaPura);
          if (!cuotaPuraValue || cuotaPuraValue <= 0) {
            const errorResult = {
              message: "Debe ingresar una cuota pura válida para enviar deuda.",
              summary: {
                total_emails: 0,
                successful_count: 0,
                failed_count: 0
              },
              successful_emails: [],
              failed_emails: []
            };
            setEmailResults(errorResult);
            setEmailResultsDialogOpen(true);
            setBulkConfirmationDialogOpen(false);
            return;
          }
          const emailsData = Array.isArray(confirmedEmails?.data)
            ? confirmedEmails.data
            : Array.isArray(confirmedEmails)
            ? confirmedEmails
            : confirmedEmails?.emails || [];
          const payload = buildDebtEmailsPayload(emailsData, cuotaPuraValue);
          result = await sendDebtMailsMutation.mutateAsync(payload);
          result = normalizeDebtResult(result);
        } else {
          result = await sendMailsMutation.mutateAsync(confirmedEmails);
        }
        
        setEmailResults(result);
        setEmailResultsDialogOpen(true);
        
        setBulkConfirmationDialogOpen(false);
        setSelectedItems([]);
        setIncludeAllSubscribers(false);
      } catch (error) {
        console.error('Error sending bulk emails:', error);

        const debtData = Array.isArray(confirmedEmails?.data)
          ? confirmedEmails.data
          : confirmedEmails?.emails_deudas;
        const errorCount =
          confirmedEmails?.emails?.length || debtData?.length || 0;
        const errorResult = {
          message: "Error al enviar los emails",
          summary: {
            total_emails: errorCount,
            successful_count: 0,
            failed_count: errorCount
          },
          successful_emails: [],
          failed_emails:
            confirmedEmails?.emails?.map(email => ({
              email: email.name || 'Desconocido',
              order_number: email.order_number || 'Desconocido',
              uuid_for_file: email.uuid_for_file || 'Desconocido',
              error: error.message || 'Error desconocido'
            })) ||
            debtData?.map(email => ({
              email: email.suscriptor || 'Desconocido',
              order_number: email.contrato_id || 'Desconocido',
              uuid_for_file: email.uuid_for_file || 'Desconocido',
              error: error.message || 'Error desconocido'
            })) ||
            []
        };
        
        setEmailResults(errorResult);
        setEmailResultsDialogOpen(true);
        setBulkConfirmationDialogOpen(false);
      }
    },
    [sendMailsMutation, sendDebtMailsMutation, filter.notCancelled, filter.cuotaPura]
  );

  const handleCloseEmailDialog = useCallback(() => {
    setEmailDialogOpen(false);
    setSelectedEmailData({});
  }, []);

  const handleCloseBulkConfirmationDialog = useCallback(() => {
    setBulkConfirmationDialogOpen(false);
    setSelectedItems([]);
  }, []);

  const handleCloseEmailResultsDialog = useCallback(() => {
    setEmailResultsDialogOpen(false);
    setEmailResults(null);
  }, []);

  const handleIndividualEmailResult = useCallback((result) => {
    setEmailResults(result);
    setEmailResultsDialogOpen(true);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
  }, []);

  useEffect(() => {
    if (!bulkConfirmationDialogOpen) {
      clearSelection();
    }
  }, [bulkConfirmationDialogOpen, clearSelection]);

  return {
    // Datos principales
    data,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    filter,

    // Estados de diálogos
    emailDialogOpen,
    bulkConfirmationDialogOpen,
    emailResultsDialogOpen,
    selectedEmailData,
    emailResults,

    // Estados de selección
    selectedItems,
    includeAllSubscribers,
    bulkItems,
    bulkTotalCount,
    isAllSubscribersLoading,
    isSendingBulk,
    canBulkSend,

    // Datos externos
    plans,
    groups,

    // Handlers
    handleFilter,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleSendEmail,
    handleSelectItem,
    handleIncludeAllSubscribersChange,
    handleBulkSend,
    handleBulkSendConfirmation,
    handleCloseEmailDialog,
    handleCloseBulkConfirmationDialog,
    handleCloseEmailResultsDialog,
    handleIndividualEmailResult,
  };
};
